import { useEffect, useMemo, useState } from 'react';
import { ApiError } from '../api/client';
import {
  getFinanceTrzbyRealPrehled,
  getFinanceTrzbyRealInvoices,
  type TrzbyPrehledResult,
  type TrzbyInvoicesResult,
} from '../api/rondo';
import { useTabParam } from '../shell/useTabParam';
import { ServiceBar } from '../shell/ServiceBar';
import { ArcTabs } from '../components/arc/primitives/arc-primitives';
import { Card } from '../components/arc/card/card';
import { Button } from '../components/arc/button/button';
import { PageState } from '../components/arc/page-state/page-state';
import { SortableDataTable, type DataColumn } from '../components/arc/sortable-data-table/sortable-data-table';
import './finance-trzby-real.css';

type Tab = 'prehled' | 'vypis';
const TRZBY_REAL_TABS: readonly Tab[] = ['prehled', 'vypis'];
type VypisMode = 'firmy' | 'mesic';

const MONTHS_CS = [
  'Leden', 'Únor', 'Březen', 'Duben', 'Květen', 'Červen',
  'Červenec', 'Srpen', 'Září', 'Říjen', 'Listopad', 'Prosinec',
];
const REGION_FLAG: Record<string, string> = { CZ: '🇨🇿', SK: '🇸🇰', EU: '🇪🇺' };

const FX: Record<string, number> = { CZK: 1, EUR: 25, USD: 22, RON: 5 };
const toCzk = (amount: number, cur: string) => amount * (FX[cur] ?? 1);

const fmtKc = (n: number) =>
  `${Math.round(n).toLocaleString('cs-CZ')} Kč`;
const fmtCur = (n: number, cur: string) =>
  `${Math.round(n).toLocaleString('cs-CZ')} ${cur}`;

export function FinanceTrzbyRealPage() {
  const [tab, setTab] = useTabParam<Tab>('tab', TRZBY_REAL_TABS, 'prehled');
  return (
    <section className="rondo-real-revenue rondo-arc-real p1-arc-scope">
      <header className="prototype-page-head"><div><div className="prototype-eyebrow rondo">FINANCE</div><h1>Tržby z reklam</h1><p>Fakturace, úhrady a výnosy reklamních partnerů</p></div></header>
      <ArcTabs label="Tržby z reklam" items={[{ value: 'prehled', label: 'Přehled' }, { value: 'vypis', label: 'Výpis' }]} value={tab} onChange={setTab} className="rondo-real-arc-tabs" />
      {tab === 'prehled' ? <PrehledTab /> : <VypisTab />}
    </section>
  );
}

function PrehledTab() {
  const now = new Date();
  const year = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  const month = now.getMonth() === 0 ? 12 : now.getMonth();
  const [data, setData] = useState<TrzbyPrehledResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [inCzk, setInCzk] = useState(() => {
    try { return localStorage.getItem('ftr-prehled-czk') === '1'; } catch { return false; }
  });
  const [refreshing, setRefreshing] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [prevCount, setPrevCount] = useState<number | null>(null);

  const prevY = month === 1 ? year - 1 : year;
  const prevM = month === 1 ? 12 : month - 1;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      getFinanceTrzbyRealPrehled({ year, month }),
      getFinanceTrzbyRealPrehled({ year: prevY, month: prevM }).catch(() => null),
    ])
      .then(([d, prev]) => {
        if (cancelled) return;
        setData(d);
        setPrevCount(prev ? prev.perKlient.reduce((s, r) => s + r.cnt, 0) : null);
      })
      .catch((e: unknown) => !cancelled && setError(e instanceof ApiError ? e.message : 'Načtení selhalo'))
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        setRefreshing(false);
        setRefreshedAt(new Date().toLocaleTimeString('cs-CZ'));
      });
    return () => { cancelled = true; };
  }, [year, month, prevY, prevM, tick]);

  // Per-klient agregace. POZOR 1:1 legacy zvláštnost: net = PROSTÝ součet
  // net_amount přes všechny faktury klienta (i různé měny smíchané), currency
  // = měna PRVNÍ faktury. Při "Zobrazit v CZK" se ten smíchaný součet násobí
  // kurzem první měny. (Legacy finance-trzby-real.html - záměrně zachováno
  // pro datovou shodu s prod, byť měnově nepřesné.)
  const klientCards = useMemo(() => {
    if (!data) return [];
    const klMap = new Map(data.klienti.map((k) => [k.id, k]));
    const agg = new Map<number, { name: string; region: string; cnt: number; net: number; currency: string }>();
    for (const r of data.perKlient) {
      const k = klMap.get(r.klient_id);
      if (!k) continue;
      if (!agg.has(r.klient_id)) agg.set(r.klient_id, { name: k.name, region: k.region, cnt: 0, net: 0, currency: r.currency });
      const e = agg.get(r.klient_id)!;
      e.cnt += r.cnt;
      e.net += r.net;
    }
    return [...agg.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  const mtd = useMemo(() => {
    const byCur: Record<string, number> = {};
    let count = 0;
    if (data) for (const r of data.perKlient) { byCur[r.currency] = (byCur[r.currency] ?? 0) + r.net; count += r.cnt; }
    return { byCur, count };
  }, [data]);

  const unpaidTotal = useMemo(() => {
    const byCur: Record<string, number> = {};
    let count = 0;
    if (data) for (const u of data.unpaid) { byCur[u.currency] = (byCur[u.currency] ?? 0) + u.unpaid; count += u.cnt; }
    return { byCur, count };
  }, [data]);

  const fmtTotals = (byCur: Record<string, number>) => {
    const entries = Object.entries(byCur);
    if (!entries.length) return fmtKc(0);
    return inCzk
      ? fmtKc(entries.reduce((s, [c, v]) => s + toCzk(v, c), 0))
      : entries.map(([c, v]) => fmtCur(v, c)).join(' + ');
  };

  const monthLabel = `${MONTHS_CS[month - 1]} ${year}`;
  const syncAt = data?.sync?.last_sync_at
    ? new Date(data.sync.last_sync_at).toLocaleString('cs-CZ', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
    : null;

  const onRefresh = () => { setRefreshing(true); setTick((t) => t + 1); };

  return (
    <>
      <div className="real-overview-toolbar">
          <div className="real-period-compact"><span className="finance-context-dot" /><div><small>Aktuální měsíc</small><strong>{monthLabel}</strong></div></div>
          <div className="real-period-actions">
            <div className="form-check form-switch m-0 real-switch">
              <input className="form-check-input" type="checkbox" id="ftr-czk" checked={inCzk} onChange={(e) => { setInCzk(e.target.checked); try { localStorage.setItem('ftr-prehled-czk', e.target.checked ? '1' : '0'); } catch { /* ignore */ } }} style={{ cursor: 'pointer' }} />
              <label className="form-check-label small text-muted" htmlFor="ftr-czk" style={{ cursor: 'pointer' }}>Zobrazit v CZK</label>
            </div>
            <Button type="button" variant="secondary" size="sm" className="real-refresh" onClick={onRefresh} loading={refreshing}>
              {refreshing ? 'Načítám…' : 'Obnovit'}
            </Button>
            {(refreshing || refreshedAt) && (
              <span className="real-updated">
                {refreshing ? 'Načítám…' : `Aktualizováno ${refreshedAt}`}
              </span>
            )}
            {syncAt && (
              <div className="real-sync">
                Synchronizováno {syncAt} · {mtd.count} faktur tento měsíc{prevCount != null ? ` · ${prevCount} minulý` : ''}
              </div>
            )}
          </div>
      </div>

      {error ? (
        <PageState title="Tržby z reklam se nepodařilo načíst" message={error} />
      ) : loading ? (
        <PageState title="Načítám tržby z reklam" loading />
      ) : (
        <>
          {data && !data.fioBank.ok && (
            <Card title="Bankovní data nejsou aktuální" className="rondo-arc-real-warning">
              <p>Stav „zaplaceno" u přijatých faktur
              nemusí být správný - FIO sync účtů{' '}
              {data.fioBank.staleAccounts
                .map((a) => `#${a.id} (${a.status ?? '-'}${a.lastSyncAt ? `, poslední ${new Date(a.lastSyncAt).toLocaleString('cs-CZ')}` : ''})`)
                .join(', ')}
              {' '}selhal, chybí nebo je zastaralý.</p>
            </Card>
          )}
          <div className="rondo-arc-real-kpis">
            <Card title="Tržby za měsíc" description={monthLabel} className="rondo-arc-real-kpi"><strong>{fmtTotals(mtd.byCur)}</strong><span>{mtd.count} faktur</span></Card>
            <Card title="Čekáme na úhradu" description={monthLabel} className="rondo-arc-real-kpi"><strong>{fmtTotals(unpaidTotal.byCur)}</strong><span>{unpaidTotal.count} otevřených faktur</span></Card>
          </div>

          {klientCards.length === 0 ? (
            <PageState title="Žádné faktury v aktuálním měsíci" />
          ) : (
            <div className="rondo-arc-real-client-grid">
              {klientCards.map((c) => (
                <Card key={c.name} title={c.name} description={`${REGION_FLAG[c.region] ?? ''} ${c.region}`} className="rondo-arc-real-client-card">
                  <strong>{inCzk ? fmtKc(toCzk(c.net, c.currency)) : fmtCur(c.net, c.currency)}</strong>
                  <span>{c.cnt} faktur</span>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </>
  );
}

function VypisTab() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState<number | ''>('');
  const [klientId, setKlientId] = useState<number | ''>('');
  const [mode, setMode] = useState<VypisMode>('firmy');
  const [inCzk, setInCzk] = useState(false);
  const [onlyPaid, setOnlyPaid] = useState(false);
  const [data, setData] = useState<TrzbyInvoicesResult | null>(null);
  const [klienti, setKlienti] = useState<{ id: number; name: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getFinanceTrzbyRealPrehled({ year: now.getFullYear(), month: now.getMonth() + 1 })
      .then((d) => setKlienti(d.klienti.map((k) => ({ id: k.id, name: k.name })).sort((a, b) => a.name.localeCompare(b.name))))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getFinanceTrzbyRealInvoices({ year, month: month === '' ? null : month, klientId: klientId === '' ? null : klientId })
      .then((d) => !cancelled && setData(d))
      .catch((e: unknown) => !cancelled && setError(e instanceof ApiError ? e.message : 'Načtení selhalo'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [year, month, klientId]);

  const invoices = useMemo(() => {
    let list = data?.invoices ?? [];
    if (onlyPaid) list = list.filter((i) => (i.unpaid_amount ?? 0) <= 0.005 && !i.cancelled);
    return list;
  }, [data, onlyPaid]);

  const amt = (n: number, cur: string) => (inCzk ? fmtKc(toCzk(n, cur)) : fmtCur(n, cur));

  const byMonth = useMemo(() => {
    const m = new Map<string, { label: string; cnt: number; net: number; paid: number; unpaid: number }>();
    for (const i of invoices) {
      const key = (i.duzp ?? '').slice(0, 7);
      if (!key) continue;
      const [y, mm] = key.split('-');
      const label = `${MONTHS_CS[Number(mm) - 1]} ${y}`;
      const e = m.get(key) ?? { label, cnt: 0, net: 0, paid: 0, unpaid: 0 };
      e.cnt += 1;
      e.net += toCzk(i.net_amount, i.currency);
      e.paid += toCzk(i.paid_amount ?? 0, i.currency);
      e.unpaid += toCzk(i.unpaid_amount ?? 0, i.currency);
      m.set(key, e);
    }
    return [...m.entries()].sort((a, b) => b[0].localeCompare(a[0])).map(([, v]) => v);
  }, [invoices]);

  const monthRows = byMonth.map((item, index) => ({ ...item, key: `${item.label}-${index}` }));
  type MonthRow = (typeof monthRows)[number];
  const monthColumns: DataColumn<MonthRow>[] = [
    { key: 'label', label: 'Měsíc (DUZP)', width: 220 },
    { key: 'cnt', label: 'Počet faktur', numeric: true, width: 160 },
    { key: 'net', label: 'Celkem bez DPH', numeric: true, width: 200, render: (_, row) => fmtKc(row.net) },
    { key: 'paid', label: 'Uhrazeno', numeric: true, width: 180, render: (_, row) => row.paid > 0.005 ? fmtKc(row.paid) : '—' },
    { key: 'unpaid', label: 'Zbývá', numeric: true, width: 180, render: (_, row) => row.unpaid > 0.005 ? fmtKc(row.unpaid) : '—' },
  ];
  const invoiceRows = invoices.map((inv) => ({
    ...inv,
    monthName: inv.duzp ? MONTHS_CS[Number(inv.duzp.slice(5, 7)) - 1] ?? '—' : '—',
    paidStatus: inv.cancelled ? 'Storno' : (inv.unpaid_amount ?? 0) <= 0.005 ? 'Uhrazeno' : 'Neuhrazeno',
  }));
  type InvoiceRow = (typeof invoiceRows)[number];
  const invoiceColumns: DataColumn<InvoiceRow>[] = [
    { key: 'duzp', label: 'DUZP', width: 135, render: (_, row) => row.duzp ?? '—' },
    { key: 'monthName', label: 'Měsíc', width: 90 },
    { key: 'klient_name', label: 'Klient', width: 225, render: (_, row) => `${REGION_FLAG[row.region] ?? ''} ${row.klient_name}` },
    { key: 'public_id', label: 'Faktura', width: 135, render: (_, row) => row.public_id ?? '—' },
    { key: 'net_amount', label: 'Bez DPH', numeric: true, width: 140, render: (_, row) => amt(row.net_amount, row.currency) },
    { key: 'currency', label: 'Měna', width: 70 },
    { key: 'due_date', label: 'Splatnost', width: 110, render: (_, row) => row.due_date ?? '—' },
    { key: 'paidStatus', label: 'Stav', width: 115 },
    { key: 'paid_date', label: 'Zaplaceno', width: 110, render: (_, row) => (row.unpaid_amount ?? 0) <= 0.005 ? row.paid_date ?? '—' : '—' },
  ];

  return (
    <>
      <ServiceBar variant="seg" selects={[
        { label:'Rok', value:String(year), onChange:(v)=>setYear(Number(v)), options:[now.getFullYear(),now.getFullYear()-1,now.getFullYear()-2,now.getFullYear()-3].map(y=>({value:String(y),label:String(y)})) },
        { label:'Měsíc', value:String(month), onChange:(v)=>setMonth(v===''?'':Number(v)), options:[{value:'',label:'Celý rok'},...MONTHS_CS.map((m,i)=>({value:String(i+1),label:`${String(i+1).padStart(2,'0')} ${m}`}))] },
        { label:'Klient', value:String(klientId), onChange:(v)=>setKlientId(v===''?'':Number(v)), options:[{value:'',label:'Všichni'},...klienti.map(k=>({value:String(k.id),label:k.name}))], minWidth:180 },
        { label:'Zobrazení', value:mode, onChange:(v)=>setMode(v as VypisMode), options:[{value:'firmy',label:'Podle firem'},{value:'mesic',label:'Podle měsíce'}] },
      ]} onReset={()=>{setYear(now.getFullYear());setMonth('');setKlientId('');setMode('firmy');setInCzk(false);setOnlyPaid(false)}} extra={<>
            <label className="real-check">
              <input type="checkbox" checked={inCzk} onChange={(e) => setInCzk(e.target.checked)} /> Zobrazit v CZK
            </label>
            <label className="real-check">
              <input type="checkbox" checked={onlyPaid} onChange={(e) => setOnlyPaid(e.target.checked)} /> Jen uhrazené
            </label>
          </>} right={<span className="real-list-meta">{loading ? 'Načítám…' : `${invoices.length} faktur`}</span>} />

      <div className={`rondo-arc-real-table ${mode === 'mesic' ? 'rondo-arc-real-month-table' : 'rondo-arc-real-invoice-table'}`}>
        {error && <PageState title="Výpis se nepodařilo načíst" message={error} />}
        {loading ? <PageState title="Načítám výpis" loading /> : !error && (mode === 'mesic'
          ? <SortableDataTable rows={monthRows} columns={monthColumns} rowKey="key" caption="Tržby po měsících" emptyMessage="Žádné faktury." />
          : <SortableDataTable rows={invoiceRows} columns={invoiceColumns} rowKey="id" caption="Tržby podle firem" emptyMessage="Žádné faktury." />)}
      </div>
    </>
  );
}
